import type {
  ApiSuccess,
  DiscoveryFeed,
  DiscoveryFilters,
  DiscoverySearchResult,
  RestaurantPage,
  SearchSort,
  Serviceability,
  Town,
} from '@capsico/contracts';
import { api } from './client';

export interface Point {
  lat: number;
  lng: number;
}

export async function fetchFeed(point: Point): Promise<DiscoveryFeed> {
  const res = await api.get<ApiSuccess<DiscoveryFeed>>('/v1/discover/feed', { params: point });
  return res.data.data;
}

/** Only switched-on filters go in the query string (the API treats a missing flag as off). */
function filterParams(filters: DiscoveryFilters): Record<string, string> {
  const params: Record<string, string> = {};
  const flags = ['veg', 'pureVeg', 'rating4', 'fast', 'offers', 'newOnly', 'openNow'] as const;
  for (const flag of flags) if (filters[flag]) params[flag] = 'true';
  if (filters.priceBand) params['priceBand'] = filters.priceBand;
  if (filters.cuisine) params['cuisine'] = filters.cuisine;
  return params;
}

export async function searchOutlets(input: {
  point: Point;
  q: string;
  sort: SearchSort;
  filters: DiscoveryFilters;
}): Promise<DiscoverySearchResult> {
  const res = await api.get<ApiSuccess<DiscoverySearchResult>>('/v1/discover/search', {
    params: {
      lat: input.point.lat,
      lng: input.point.lng,
      ...(input.q.trim() ? { q: input.q.trim() } : {}),
      sort: input.sort,
      ...filterParams(input.filters),
    },
  });
  return res.data.data;
}

export async function fetchRestaurant(outletId: string, point: Point): Promise<RestaurantPage> {
  const res = await api.get<ApiSuccess<RestaurantPage>>(`/v1/discover/outlets/${outletId}`, {
    params: point,
  });
  return res.data.data;
}

/* Location helpers (public geo reads) */

export async function fetchServiceability(point: Point): Promise<Serviceability> {
  const res = await api.get<ApiSuccess<Serviceability>>('/v1/geo/serviceability', {
    params: point,
  });
  return res.data.data;
}

export async function fetchLiveTowns(): Promise<Town[]> {
  const res = await api.get<ApiSuccess<Town[]>>('/v1/geo/towns', { params: { status: 'live' } });
  return res.data.data;
}
